import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ProfileSchema } from '../lib/profile-schema';
import ReactInputMask from 'react-input-mask';
import { InputField } from '@shared/ui/input-field';
import { updateMe } from '@entities/auth/model';
import { formatPhoneNumber } from '@shared/lib/validators/phone-normalize';
import { User } from '../../../types/interfaces';
import { useAppDispatch, useAppSelector } from '@app/store/store';

interface RegistrationFormProps {
  me: User;
  title?: string;
}

export const RegistrationForm = ({
  me,
  title = '',
}: RegistrationFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(ProfileSchema),
    shouldUnregister: false,
    defaultValues: {
      first_name: me?.first_name,
      mobile_phone: formatPhoneNumber(me?.mobile_phone ?? ''),
      patronymic: me?.patronymic ? me?.patronymic : undefined,
      last_name: me?.last_name,
      telegram: me?.telegram ? me?.telegram : undefined,
      whatsapp: me?.whatsapp ? me?.whatsapp : undefined,
      additional_phone:
        formatPhoneNumber(me?.additional_phone ?? '') ?? undefined,
    },
  });

  const status = useAppSelector((state) => state.auth.status);
  const dispatch = useAppDispatch();

  const onSubmit = (data: Record<string, unknown>) => {
    if ('email' in data) delete (data as Record<string, unknown>).email;
    if ('username' in data) delete (data as Record<string, unknown>).username;
    dispatch(updateMe({ data }));
  };

  const onCancel = () => {
   
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {title ? (
        <h3 className="text-base font-semibold text-[#2d2c2c]">{title}</h3>
      ) : null}

      {/* ── Секция 1. Личные данные ───────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold text-[#2d2c2c]">Личные данные</h2>
        <p className="text-sm text-gray-500 mt-0.5 mb-4">
          Эти поля отображаются другим пользователям системы.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
          <InputField
            id="last_name"
            inputName="last_name"
            label="Фамилия *"
            errors={errors}
            register={register}
          />
          <InputField
            id="first_name"
            inputName="first_name"
            label="Имя *"
            errors={errors}
            register={register}
          />
          <InputField
            id="patronymic"
            inputName="patronymic"
            label="Отчество"
            errors={errors}
            register={register}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mt-3">
          <Controller
            name="mobile_phone"
            control={control}
            render={({ field }) => (
              <InputField
                {...field}
                as={ReactInputMask}
                mask={'+7 (***) ***-**-**'}
                id="mobile_phone"
                inputName="mobile_phone"
                label="Телефон *"
                errors={errors}
                register={register}
              />
            )}
          />
          <Controller
            name="additional_phone"
            control={control}
            render={({ field }) => (
              <InputField
                {...field}
                as={ReactInputMask}
                mask={'+7 (***) ***-**-**'}
                id="additional_phone"
                inputName="additional_phone"
                label="Доп.телефон"
                errors={errors}
                register={register}
              />
            )}
          />
        </div>

        {/* Мессенджеры — необязательные, форма принимает их как просто
            строки (валидация — в profile-schema.js). По дизайну тот
            же стиль, что у телефонов: 2 колонки. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mt-3">
          <InputField
            id="telegram"
            inputName="telegram"
            label="Telegram"
            type="text"
            register={register}
            errors={errors}
          />
          <InputField
            id="whatsapp"
            inputName="whatsapp"
            label="WhatsApp"
            type="text"
            register={register}
            errors={errors}
          />
        </div>
      </div>

      {/* ── Кнопки внизу справа, как на макете ─────────────────── */}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={!isDirty || status === 'loading'}
          className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 border-0 cursor-pointer hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Отменить
        </button>
        <button
          type="submit"
          disabled={(!isValid && status === 'loading') || !isDirty || status === 'loading'}
          className="px-4 py-2 text-sm rounded-lg bg-[#e3282a] text-white border-0 cursor-pointer hover:bg-[#c91f21] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Сохранить
        </button>
      </div>
    </form>
  );
};
